import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {catchError, concatMap, filter, map, mergeMap, tap, withLatestFrom} from 'rxjs/operators';
import * as GlobalMessageActions from '../../core/actions/global-message.actions';
import {GlobalMessage, GlobalMessageType} from '../../core/models/global-message';
import * as ProductActions from '../../products/actions/product.actions';
import {LoadingCollection} from '../../products/models/product';
import * as fromProducts from '../../products/reducers';
import {AbstractAction, CommandActionPayload} from '../../shared/actions/abstract-actions';
import * as QuoteActions from '../actions/quote.action';
import {QuoteProductCommand} from '../model/quote-commands';
import {QuotesError} from '../model/quotes-error';
import {QuoteProductService} from '../services/quote-product.service';


const filterLoadingProducts = (loadingCollection: Observable<LoadingCollection>) => {
    return (source: Observable<any>) => {
        return source.pipe(
            tap(e => console.log(e)),
            withLatestFrom(loadingCollection,
                (action: AbstractAction<QuoteProductCommand, CommandActionPayload<QuoteProductCommand>>, collection) => {
                    return {
                        quoteProductAction: action,
                        isLoading: collection[action.command.product.id]
                    };
                }),
            filter(action => !action.isLoading),
        );
    };
};

@Injectable()
export class QuoteProductEffects {

    addProductToQuoteTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.addProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        map((action) => ProductActions.actionStart({product: action.command.product}))
    ));

    addProductToQuote$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.addProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        concatMap((action) => {
            return this.service.addProductToQuote(action.command)
                .pipe(
                    map(quote => {
                        console.log(quote)
                        QuoteActions.changeChosenQuote({chosenQuote: quote})
                        return QuoteActions.addProductSuccess({command: action.command, quote: quote})
                    }),
                    catchError(error => of(QuoteActions.addProductFailure({
                        command: action.command, error: (<QuotesError>error).errorMessage
                    })))
                );
        })
    ));

    addProductToQuoteFailure$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.addProductFailure),
        tap(action => console.log('Quote add product failure', action)),
        mergeMap((action) => {
                const message: GlobalMessage = {
                    id: `quote-${action.command.quote.id}-add-product-${action.command.product.id}-error`,
                    type: GlobalMessageType.ERROR,
                    message: `The product '${action.command.product.name}' couldn't be added to the quote '${action.command.quote.name}': ${action.error}`
                };
                return of(GlobalMessageActions.addMessage({globalMessage: message}));
            }
        )
    ));

    addProductToQuoteSuccessTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.addProductSuccess),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionSuccess(payload))
    ));

    addProductToQuoteFailureTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.addProductFailure),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionError(payload))
    ));

    // edit product

    editProductFromQuoteTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.editProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        map((action) => ProductActions.actionStart({product: action.command.product}))
    ));

    editProductFromQuote$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.editProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        concatMap((action) => {
            return this.service.editProductFromQuote(action.command)
                .pipe(
                    map(quote => QuoteActions.editProductSuccess({command: action.command, quote: quote})),
                    catchError(error => of(QuoteActions.editProductFailure({
                        command: action.command, error: (<QuotesError>error).errorMessage
                    })))
                );
        })
    ));

    editProductFromQuoteFailure$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.editProductFailure),
        tap(action => console.log('Quote edit product failure', action)),
        mergeMap((action) => {
                const message: GlobalMessage = {
                    id: `quote-${action.command.quote.id}-edit-product-${action.command.product.id}-error`,
                    type: GlobalMessageType.ERROR,
                    message: `The product '${action.command.product.name}' couldn't be edited at the quote '${action.command.quote.name}': ${action.error}`
                };
                return of(GlobalMessageActions.addMessage({globalMessage: message}));
            }
        )
    ));

    editProductFromQuoteSuccessTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.editProductSuccess),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionSuccess(payload))
    ));

    editProductFromQuoteFailureTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.editProductFailure),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionError(payload))
    ));


    // delete product

    deleteProductFromQuoteTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.deleteProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        map((action) => ProductActions.actionStart({product: action.command.product}))
    ));

    deleteProductFromQuote$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.deleteProduct),
        filterLoadingProducts(this.store.pipe(select(fromProducts.getLoadingCollection))),
        map(action => action.quoteProductAction),
        concatMap((action) => {
            return this.service.deleteProductFromQuote(action.command)
                .pipe(
                    map(quote => {
                        QuoteActions.changeChosenQuote({chosenQuote: quote});
                        return QuoteActions.deleteProductSuccess({command: action.command, quote: quote})
                    }),
                    catchError(error => of(QuoteActions.deleteProductFailure({
                        command: action.command, error: (<QuotesError>error).errorMessage
                    })))
                );
        })
    ));

    deleteProductFromQuoteFailure$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.deleteProductFailure),
        tap(action => console.log('Quote delete product failure', action)),
        mergeMap((action) => {
                const message: GlobalMessage = {
                    id: `quote-${action.command.quote.id}-delete-product-${action.command.product.id}-error`,
                    type: GlobalMessageType.ERROR,
                    message: `The product '${action.command.product.name}' couldn't be deleted from the quote '${action.command.quote.name}': ${action.error}`
                };
                return of(GlobalMessageActions.addMessage({globalMessage: message}));
            }
        )
    ));

    deleteProductFromQuoteSuccessTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.deleteProductSuccess),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionSuccess(payload))
    ));

    deleteProductFromQuoteFailureTrigger$ = createEffect(() => this.actions$.pipe(
        ofType(QuoteActions.deleteProductFailure),
        map((action) => {
            return {
                product: action.command.product,
                message: ''
            };
        }),
        map(payload => ProductActions.actionError(payload))
    ));

    constructor(private actions$: Actions, private service: QuoteProductService,
                private store: Store<fromProducts.State>) {
    }
}
